import { c as _c } from "react/compiler-runtime";
import { IconChatBubbleOutline18, IconCircleInfoOutline18, IconCopyOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import ConfirmModal from "@/components/custom/ConfirmModal";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import type { FixWithAIErrorContext } from "@/hooks/useFixWithAI";
import type { NodeExecutionState } from "@/types/runStream";
import React from "react";
import { InsufficientBalanceModal, isInsufficientBalanceError } from "./InsufficientBalanceModal";
import { ErrorDetailModal } from "./NodeExecutionDetails";
import NodeInfoModal from "./NodeInfoModal";
interface ActionNodeContextMenuProps {
  children: React.ReactNode;
  /** Node ID */
  id: string;
  /** Handler to open info modal */
  onInfoOpen: () => void;
  /** Handler to duplicate the node */
  onDuplicate: () => void;
  /** Handler to add a comment */
  onAddComment?: (nodeId: string) => void;
  /** Handler to begin delete flow (opens confirmation) */
  onDelete: () => void;
}

/**
 * Context menu wrapper for ActionNode. Right-click reveals info, duplicate,
 * comment, and delete options.
 */
export function ActionNodeContextMenuContent(t0) {
  const $ = _c(23);
  const {
    id,
    onInfoOpen,
    onDuplicate,
    onAddComment,
    onDelete
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconCircleInfoOutline18 className="mr-2 h-4 w-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onInfoOpen) {
    t2 = <ContextMenuItem onClick={onInfoOpen} className="cursor-pointer">{t1}More info</ContextMenuItem>;
    $[1] = onInfoOpen;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ContextMenuSeparator />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconCopyOutline18 className="mr-2 h-4 w-4" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onDuplicate) {
    t5 = <ContextMenuItem onClick={onDuplicate} className="cursor-pointer">{t4}Duplicate</ContextMenuItem>;
    $[5] = onDuplicate;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ContextMenuSeparator />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== id || $[9] !== onAddComment) {
    t7 = () => {
      onAddComment?.(id);
    };
    $[8] = id;
    $[9] = onAddComment;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IconChatBubbleOutline18 className="mr-2 h-4 w-4" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <ContextMenuItem onClick={t7} className="cursor-pointer">{t8}Add Comment</ContextMenuItem>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ContextMenuSeparator />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <IconTrashOutline18 className="mr-2 h-4 w-4" />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== onDelete) {
    t12 = <ContextMenuItem onClick={onDelete} className="cursor-pointer text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400">{t11}Delete Action</ContextMenuItem>;
    $[16] = onDelete;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t2 || $[20] !== t5 || $[21] !== t9) {
    t13 = <ContextMenuContent className="w-56">{t2}{t3}{t5}{t6}{t9}{t10}{t12}</ContextMenuContent>;
    $[18] = t12;
    $[19] = t2;
    $[20] = t5;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
}

// ─── Modals ─────────────────────────────────────────────────────────────────

interface ActionNodeModalsProps {
  /** Action definition (for requiredTokens in InsufficientBalanceModal) */
  definition?: ActionTriggerDefinition;
  /** Node payload — used to infer gas/token requirements */
  nodePayload?: Record<string, unknown>;
  /** Error detail modal */
  errorModalOpen: boolean;
  onErrorModalChange: (open: boolean) => void;
  nodeInfo: {
    name?: string;
    type?: string;
    description?: string;
    category: "action";
  };
  nodeState?: NodeExecutionState;
  onFixWithAI: (errorCtx?: FixWithAIErrorContext) => void;
  /** Called when AI analysis suggests a field-level fix to apply */
  onFieldUpdate?: (fieldName: string, value: unknown) => void;
  /** Workflow/run context for version compare and AI analysis */
  workflowId?: string;
  runId?: string;
  /** Called when user clicks rollback to a previous version */
  onRollback?: (targetVersion: number) => void;
  /** Delete confirmation modal */
  showDeleteConfirm: boolean;
  onDeleteConfirmChange: (open: boolean) => void;
  onConfirmDelete: () => void;
  /** Info modal */
  infoModalOpen: boolean;
  onInfoModalChange: (open: boolean) => void;
  modalDefinition: React.ComponentProps<typeof NodeInfoModal>["node"];
}

/**
 * All modal dialogs rendered by ActionNode: error detail, delete confirmation,
 * and node info modal.
 */
export function ActionNodeModals(t0) {
  const $ = _c(34);
  const {
    definition,
    nodePayload,
    errorModalOpen,
    onErrorModalChange,
    nodeInfo,
    nodeState,
    onFixWithAI,
    onFieldUpdate,
    workflowId,
    runId,
    onRollback,
    showDeleteConfirm,
    onDeleteConfirmChange,
    onConfirmDelete,
    infoModalOpen,
    onInfoModalChange,
    modalDefinition
  } = t0;
  const t1 = nodeState?.error;
  let t2;
  if ($[0] !== t1) {
    t2 = isInsufficientBalanceError(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const showInsufficientBalance = t2;
  let t3;
  if ($[2] !== definition || $[3] !== errorModalOpen || $[4] !== nodePayload || $[5] !== nodeState?.error || $[6] !== onErrorModalChange || $[7] !== showInsufficientBalance) {
    t3 = showInsufficientBalance && <InsufficientBalanceModal open={errorModalOpen} onOpenChange={onErrorModalChange} errorStr={nodeState?.error} definition={definition} nodePayload={nodePayload} />;
    $[2] = definition;
    $[3] = errorModalOpen;
    $[4] = nodePayload;
    $[5] = nodeState?.error;
    $[6] = onErrorModalChange;
    $[7] = showInsufficientBalance;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== errorModalOpen || $[10] !== nodeInfo || $[11] !== nodePayload || $[12] !== nodeState || $[13] !== onErrorModalChange || $[14] !== onFieldUpdate || $[15] !== onFixWithAI || $[16] !== onRollback || $[17] !== runId || $[18] !== showInsufficientBalance || $[19] !== workflowId) {
    t4 = !showInsufficientBalance && <ErrorDetailModal open={errorModalOpen} onOpenChange={onErrorModalChange} nodeInfo={nodeInfo} nodeState={nodeState} nodeConfig={nodePayload} onFixWithAI={onFixWithAI} onFieldUpdate={onFieldUpdate} workflowId={workflowId} runId={runId} onRollback={onRollback} />;
    $[9] = errorModalOpen;
    $[10] = nodeInfo;
    $[11] = nodePayload;
    $[12] = nodeState;
    $[13] = onErrorModalChange;
    $[14] = onFieldUpdate;
    $[15] = onFixWithAI;
    $[16] = onRollback;
    $[17] = runId;
    $[18] = showInsufficientBalance;
    $[19] = workflowId;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== onConfirmDelete || $[22] !== onDeleteConfirmChange || $[23] !== showDeleteConfirm) {
    t5 = <ConfirmModal open={showDeleteConfirm} onOpenChange={onDeleteConfirmChange} title="Delete action?" description="This will remove the action from your workflow." confirmText="Delete" variant="destructive" onConfirm={onConfirmDelete} />;
    $[21] = onConfirmDelete;
    $[22] = onDeleteConfirmChange;
    $[23] = showDeleteConfirm;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const t6 = modalDefinition ?? null;
  let t7;
  if ($[25] !== infoModalOpen || $[26] !== onInfoModalChange || $[27] !== t6) {
    t7 = <NodeInfoModal open={infoModalOpen} onOpenChange={onInfoModalChange} node={t6} nodeType="action" />;
    $[25] = infoModalOpen;
    $[26] = onInfoModalChange;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t3 || $[30] !== t4 || $[31] !== t5 || $[32] !== t7) {
    t8 = <>{t3}{t4}{t5}{t7}</>;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}

// Re-export ContextMenu primitives for use in ActionNode
export { ContextMenu, ContextMenuTrigger };